import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RectangleVertical } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";

export interface CardData {
  name: string;
  type?: string;
  rarity?: "common" | "uncommon" | "rare" | "epic" | "legendary";
  cost?: number;
  effect?: string;
  image_url?: string;
  image_prompt?: string;
}

const RARITY_STYLES: Record<string, { border: string; glow: string; shimmer: string; badge: string }> = {
  common: {
    border: "border-muted-foreground/30",
    glow: "",
    shimmer: "",
    badge: "border-muted-foreground/40 text-muted-foreground",
  },
  uncommon: {
    border: "border-green-500/50",
    glow: "shadow-[0_0_8px_hsl(142_70%_45%/0.2)]",
    shimmer: "",
    badge: "border-green-500/50 text-green-400",
  },
  rare: {
    border: "border-blue-500/60",
    glow: "shadow-[0_0_12px_hsl(217_90%_60%/0.3)]",
    shimmer: "",
    badge: "border-blue-500/50 text-blue-400",
  },
  epic: {
    border: "border-purple-500/70",
    glow: "shadow-[0_0_16px_hsl(270_70%_60%/0.35)]",
    shimmer: "",
    badge: "border-purple-500/50 text-purple-400",
  },
  legendary: {
    border: "border-primary/80",
    glow: "shadow-[0_0_20px_hsl(var(--primary)/0.45)]",
    shimmer: "animate-pulse",
    badge: "border-primary/60 text-primary",
  },
};

function GameCard({ card }: { card: CardData }) {
  const rarity = card.rarity || "common";
  const style = RARITY_STYLES[rarity] || RARITY_STYLES.common;
  const hasImage = !!card.image_url;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "relative w-[72px] h-[100px] sm:w-[88px] sm:h-[124px] rounded-lg border-2 overflow-hidden flex flex-col transition-all hover:scale-105 hover:-translate-y-2 cursor-pointer shrink-0 group",
            style.border, style.glow, style.shimmer,
            "bg-background",
          )}
        >
          {/* Cost badge */}
          {card.cost != null && (
            <span className="absolute top-1 left-1 z-10 text-[10px] font-bold bg-background/90 text-primary rounded-full w-5 h-5 flex items-center justify-center border border-primary/30 shadow-sm">
              {card.cost}
            </span>
          )}

          {/* Card art area — always fills space */}
          <div className="flex-1 w-full overflow-hidden">
            {hasImage ? (
              <img
                src={card.image_url}
                alt={card.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-muted via-muted/80 to-muted-foreground/10 flex items-center justify-center">
                <RectangleVertical className="w-6 h-6 text-muted-foreground/25" />
              </div>
            )}
          </div>

          {/* Name bar at bottom with gradient overlay */}
          <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-gradient-to-t from-background via-background/90 to-transparent">
            <span className="text-[8px] sm:text-[9px] text-center font-semibold text-foreground leading-tight line-clamp-2 w-full block">
              {card.name}
            </span>
            {card.type && (
              <span className="text-[7px] text-muted-foreground capitalize block text-center">
                {card.type}
              </span>
            )}
          </div>
        </button>
      </PopoverTrigger>

      <PopoverContent className="w-64 p-0 overflow-hidden" side="top">
        {/* Large card art in popover */}
        {hasImage && (
          <div className="w-full">
            <AspectRatio ratio={2 / 3}>
              <img
                src={card.image_url}
                alt={card.name}
                className="w-full h-full object-cover"
                loading="lazy"
                onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
              />
            </AspectRatio>
          </div>
        )}
        <div className="p-3 space-y-2">
          <div className="font-medium text-sm text-foreground">{card.name}</div>
          <div className="flex gap-1.5 items-center flex-wrap">
            <Badge
              variant="outline"
              className={cn("text-[10px] capitalize", style.badge)}
            >
              {rarity}
            </Badge>
            {card.type && (
              <Badge variant="secondary" className="text-[10px] capitalize">{card.type}</Badge>
            )}
            {card.cost != null && (
              <span className="text-[10px] text-muted-foreground">Cost: {card.cost}</span>
            )}
          </div>
          {card.effect && <p className="text-xs text-muted-foreground italic">{card.effect}</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface CardHandProps {
  hand: (string | CardData)[];
  deckSize?: number;
  discardCount?: number;
}

function normalizeCard(item: string | CardData): CardData {
  if (typeof item === "string") return { name: item, rarity: "common" };
  return item;
}

export function CardHand({ hand, deckSize, discardCount }: CardHandProps) {
  const cards = hand.map(normalizeCard);

  if (cards.length === 0 && !deckSize) return null;

  return (
    <Card className="p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <RectangleVertical className="w-3 h-3" />Hand ({cards.length})
        </div>
        <div className="flex gap-2 text-[10px] text-muted-foreground">
          {deckSize != null && <span>Deck: {deckSize}</span>}
          {discardCount != null && <span>Discard: {discardCount}</span>}
        </div>
      </div>
      {cards.length > 0 ? (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
          {cards.map((card, i) => (
            <GameCard key={`${card.name}-${i}`} card={card} />
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground italic text-center">No cards in hand</p>
      )}
    </Card>
  );
}
